import type { PlateRenderNodeProps, TRenderElementProps } from '@udecode/plate'
import { useCallback, useState } from 'react'
import { useSelected } from 'slate-react'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import {
  EditorTooltip,
  TooltipButton,
  TooltipText,
} from '~publish/legacy/editor/components/EditorTooltip'
import type { LinkedInAnnotationInputElement } from '../nodes/LinkedInAnnotationInputElement'
import styles from './LinkedInMentionUI.module.css'
import { Button, CoachMark, SimpleSpinner } from '@buffer-mono/popcorn'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import { useSaveCallbacks } from '~publish/legacy/composer/composer/utils/useSaveCallbacks'
import { getPublishUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import { Service } from '@buffer-mono/app-shell/src/exports/Orchestrator/channelConnections/types'

type LinkedInMentionUIProps = PlateRenderNodeProps<BufferValue, BufferEditor> &
  TRenderElementProps<BufferValue, LinkedInAnnotationInputElement>

export const LinkedInMentionUI = ({
  attributes,
  children,
  element,
}: LinkedInMentionUIProps): JSX.Element | null => {
  const selected = useSelected()
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false)
  const { saveAsDraft } = useSaveCallbacks()

  const onOpenClick = useCallback(() => {
    window.open(element.data?.link, '_blank')
  }, [element.data?.link])

  if (!element.data) {
    if (!selected) {
      return children
    }

    return (
      <>
        {element.isPermissionError ? (
          <CoachMark
            onDismiss={(): void => {
              element.isPermissionError = false
            }}
            open
          >
            <CoachMark.Overlay style={{ display: 'inline' }}>
              <span className={styles.wrapper} contentEditable={false}>
                {children}
              </span>
            </CoachMark.Overlay>

            <CoachMark.Content>
              <CoachMark.Title>
                Let&apos;s setup LinkedIn mentions!
              </CoachMark.Title>
              <CoachMark.Description>
                To get everything ready for mentioning, a quick channel refresh
                is needed.
              </CoachMark.Description>
              <CoachMark.Footer>
                <CoachMark.Dismiss>
                  <Button variant="tertiary">Not Now</Button>
                </CoachMark.Dismiss>

                <Button
                  variant="primary"
                  disabled={isSavingAsDraft}
                  onClick={(): void => {
                    const { actions } = window.appshell || {}
                    const orgData = AppStore.getOrganizationsData()
                    const channelId = element.channelId

                    setIsSavingAsDraft(true)
                    saveAsDraft()
                      .then(({ successfulResponses }) => {
                        const updateId = successfulResponses[0]?.updates.find(
                          (update) => update.profile_id === channelId,
                        )?.id

                        return actions.connectChannel({
                          selectedService: Service.linkedin,
                          isRefreshingConnection: true,
                          skipCelebrationModal: true,
                          cta: `account-channels-channelRefreshDropdown-linkedin-1`,
                          selectedRefreshChannelId: channelId,
                          role: orgData.selected.isAdmin ? 'admin' : 'member',
                          destinationUrl: getPublishUrl(
                            `channels/${channelId}${
                              updateId
                                ? `/posts/${updateId}/edit`
                                : '?tab=drafts'
                            }`,
                          ),
                        })
                      })
                      .catch(() => {
                        setIsSavingAsDraft(false)
                      })
                  }}
                >
                  {isSavingAsDraft ? (
                    <>
                      <SimpleSpinner /> Saving as Draft
                    </>
                  ) : (
                    'Refresh'
                  )}
                </Button>
              </CoachMark.Footer>
            </CoachMark.Content>
          </CoachMark>
        ) : (
          <span className={styles.wrapper}>
            {children}
            {element.children[0].text.length === 1 && selected ? (
              <span className={styles.helpText} contentEditable={false}>
                Mention a <strong>connection</strong> or paste a{' '}
                <strong>LinkedIn Page URL</strong>
              </span>
            ) : null}
            {element.isLoading && <SimpleSpinner className={styles.spinner} />}
          </span>
        )}
      </>
    )
  }

  const { link } = element.data

  // this has to be able to display both autocomplete and pasted mentions as urls
  return (
    <span className={styles.mention} {...attributes}>
      <EditorTooltip trigger={<span>{children}</span>}>
        <TooltipText title={link}>{link}</TooltipText>
        <TooltipButton onClick={onOpenClick}>Open</TooltipButton>
      </EditorTooltip>
    </span>
  )
}
